import { IAuthUser } from 'auth/types';
import { BomPartType } from 'bom/enums';
import { AccessPermissions } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { spaSendBlockTooltipKeyMap, spaStatusLabelKeyMap } from 'quotes/constants';
import {
  AttributeProjectKind,
  QuoteEntityType,
  QuoteStatus,
  SpaSendBlockReason,
  SpaStatus,
  TradeAgreementStatus,
} from 'quotes/enums';
import {
  IAttributeProject,
  IBaseQuote,
  ISpaAudience,
  ISpaAudienceRights,
  ISpaSendButtonState,
  IVendorQuote,
  IVendorQuotePartRow,
} from 'quotes/types';

// Download is never refused for the audience, only sending is.
export const ALL_SPA_AUDIENCE_RIGHTS: ISpaAudienceRights = {
  canAllowAllCustomersAllLocationsSpa: true,
  canAllowAllCustomersSpa: true,
  canAllowProgramSpa: true,
};

interface IVendorQuoteParams {
  quote?: IBaseQuote | null;
  quoteEntityType?: QuoteEntityType;
}

interface IVendorQuoteEditParams extends IVendorQuoteParams {
  hasVendorQuoteEditPermission?: boolean;
}

interface IVendorQuotePartEditParams extends IVendorQuoteEditParams {
  quotePart?: IVendorQuotePartRow | null;
}

// Line flags shared by `isQuotePartEditable` and `isQuotePartSelectPartAllowed`.
const hasNoBlockingLineFlag = (quotePart: IVendorQuotePartRow): boolean =>
  !quotePart.substituted && !quotePart.noQuote && !quotePart.locked && !quotePart.deleted;

const VALID_UNTIL_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ZERO_DATE = '0000-00-00';

export const VendorQuoteHelper = {
  // The requested-by-customer clause applies to BOTH entities; only the Imported clause is
  // QUOTE-scoped. Takes no permission input by design — an Imported VQ is maintained by
  // re-importing the vendor's document and never accepts hand-added lines, even for permitted
  // users (product rule 2026-08-05).
  canAddVendorQuoteParts: ({ quote, quoteEntityType }: IVendorQuoteParams): boolean =>
    QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER !== quote?.status &&
    !VendorQuoteHelper.isImportedVendorQuote({ quote, quoteEntityType }),

  // V1 parity with `ACL_QuoteAclService::isVendorQuoteEditable`: an Imported VQ may only be edited
  // by users whose role grants `access-company-vendor-quote-edit/*`. Kept pure — callers resolve
  // the resource through `useUserPermissions` and pass the result in.
  canEditImportedVendorQuote: ({
    quote,
    quoteEntityType = QuoteEntityType.QUOTE,
    hasVendorQuoteEditPermission = false,
  }: IVendorQuoteEditParams): boolean =>
    hasVendorQuoteEditPermission &&
    VendorQuoteHelper.isImportedVendorQuote({ quote, quoteEntityType }),

  // React rule: every vendor quote is line-editable except an Imported one, which needs the
  // vendor-quote edit resource. Deliberately diverges from V1, whose `quoteEditable`
  // (= Imported + access) hid change-part and inline price on NON-Imported VQs too — kept open
  // here pending a product decision (parity audit 2026-08-05).
  canEditVendorQuoteParts: ({
    quote,
    quoteEntityType = QuoteEntityType.QUOTE,
    hasVendorQuoteEditPermission,
  }: IVendorQuoteEditParams): boolean =>
    !VendorQuoteHelper.isImportedVendorQuote({ quote, quoteEntityType }) ||
    VendorQuoteHelper.canEditImportedVendorQuote({
      quote,
      quoteEntityType,
      hasVendorQuoteEditPermission,
    }),

  // V1 gates remove on `quoteEditable` (imported + company access), so the DRAFT/OPEN -> false leg
  // is deliberate: remove was never offered on a non-imported VQ. The ORDER leg ignores the
  // permission and keys only on ORDER_DRAFT_CUSTOMER.
  canRemoveVendorQuoteParts: ({
    quote,
    quoteEntityType = QuoteEntityType.QUOTE,
    hasVendorQuoteEditPermission,
  }: IVendorQuoteEditParams): boolean =>
    VendorQuoteHelper.canEditImportedVendorQuote({
      quote,
      quoteEntityType,
      hasVendorQuoteEditPermission,
    }) ||
    (QuoteEntityType.ORDER === quoteEntityType &&
      QuoteStatus.ORDER_DRAFT_CUSTOMER === quote?.status),

  // Company-level capability: alternate versioning on AND the vendor-quote parts export setting on.
  // Carries no per-quote status gate, so it suits surfaces with no single quote in scope (the VQ
  // list Ready-to-Activate column) and new-VQ imports (the import wizard attributes step).
  isAlternateExportEnabled: ({ user }: { user?: IAuthUser | null }): boolean => {
    const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });
    const isExportEnabled = !!user?.companySettings?.vendorQuotePartsExportEnabled;

    return isAlternateVersionEnabled && isExportEnabled;
  },

  // Null for a missing or unknown value, so callers render their dash rather than a raw wire value.
  getSpaStatusLabelKey: (status?: string | null): string | null =>
    status && Object.prototype.hasOwnProperty.call(spaStatusLabelKeyMap, status)
      ? spaStatusLabelKeyMap[status as SpaStatus]
      : null,

  // The BE's `canEditAttributes` alone decides (the predicate lives server-side, where the
  // Attributes save, the export-time persist and the re-import step enforce it).
  // Shared by the Attributes tab, the SKU export drawer and the re-import so they cannot drift.
  // No quote is not blocked (the new-import wizard); a quote without the flag is.
  isAttributesEditBlocked: (quote?: IVendorQuote | null): boolean =>
    !!quote && true !== quote.canEditAttributes,

  // Imported vendor quote on the QUOTE entity. The entity clause is hardening: BE assigns
  // EDITABLE_BY_CUSTOMER only when the VQ import flow creates a new quote, never on orders.
  isImportedVendorQuote: ({
    quote,
    quoteEntityType = QuoteEntityType.QUOTE,
  }: IVendorQuoteParams): boolean =>
    QuoteEntityType.QUOTE === quoteEntityType && QuoteStatus.EDITABLE_BY_CUSTOMER === quote?.status,

  // Exactly V1's `priceEditEnabled = quoteEditable` (imported + company access,
  // part-table.js:120): cost was never editable on a non-imported VQ in either stack — the BE
  // strips vendor-owned fields there.
  isQuotePartCostEditable: (params: IVendorQuotePartEditParams): boolean => {
    if (!params.quotePart) {
      return false;
    }

    return (
      VendorQuoteHelper.isQuotePartEditable(params) &&
      VendorQuoteHelper.canEditImportedVendorQuote(params)
    );
  },

  // Line state alone: discounts, substituted/no-quote/locked/deleted lines are never editable, and
  // an order line additionally has to still be a customer draft.
  isQuotePartEditable: ({
    quotePart,
    quoteEntityType = QuoteEntityType.QUOTE,
  }: IVendorQuotePartEditParams): boolean => {
    if (!quotePart) {
      return false;
    }

    const isEditable = BomPartType.DISCOUNT !== quotePart.type && hasNoBlockingLineFlag(quotePart);

    if (QuoteEntityType.ORDER === quoteEntityType) {
      return QuoteStatus.ORDER_DRAFT_CUSTOMER === quotePart.status && isEditable;
    }

    return isEditable;
  },

  // Deliberately the same formula as `isQuotePartCostEditable`: V1 drove change-part,
  // edit-component and inline price off the single `quoteEditable` flag
  // (quote/vendor/part-table.js:82,120), and product confirmed on 2026-08-18 that the edit-line
  // drawer keeps that one gate.
  isQuotePartEditLineAllowed: (params: IVendorQuotePartEditParams): boolean => {
    if (!params.quotePart) {
      return false;
    }

    return (
      VendorQuoteHelper.isQuotePartEditable(params) &&
      VendorQuoteHelper.canEditImportedVendorQuote(params)
    );
  },

  // V1 divergence: V1 left quantity ungated; React gates it on Imported VQs (product decision
  // 2026-08-05).
  isQuotePartQuantityEditable: (params: IVendorQuotePartEditParams): boolean => {
    if (!params.quotePart) {
      return false;
    }

    return (
      VendorQuoteHelper.isQuotePartEditable(params) &&
      VendorQuoteHelper.canEditVendorQuoteParts(params)
    );
  },

  // NOT the change-part gate (that is `isQuotePartEditLineAllowed`): this drives the attach-parts
  // affordance `PartContent` forwards as `isSelectPartAllowed` — base-code and discount lines only.
  // React keeps it open on NON-Imported VQs via canEditVendorQuoteParts where V1 hid it —
  // deliberate, pending product review (parity audit 2026-08-05). No order-line status clause,
  // unlike the editable cells.
  isQuotePartSelectPartAllowed: ({
    quotePart,
    ...quoteParams
  }: IVendorQuotePartEditParams): boolean => {
    if (!quotePart) {
      return false;
    }

    return (
      hasNoBlockingLineFlag(quotePart) && VendorQuoteHelper.canEditVendorQuoteParts(quoteParams)
    );
  },

  // V1 divergence: V1 left UoM ungated; React gates it on Imported VQs (product decision
  // 2026-08-05).
  isQuotePartUomEditable: (params: IVendorQuotePartEditParams): boolean => {
    if (!params.quotePart) {
      return false;
    }

    // An order's UoM stays open until the vendor verifies the line. Matching the quantity rule on
    // ORDER is incidental — `canEditVendorQuoteParts` is unconditionally true there.
    if (QuoteEntityType.ORDER === params.quoteEntityType) {
      return VendorQuoteHelper.isQuotePartEditable(params) && !params.quotePart.verified;
    }

    return VendorQuoteHelper.isQuotePartQuantityEditable(params);
  },

  // Per-quote availability of the Export SKU Parts action / Attributes tab: the company capability
  // plus the quote being an imported alternate (EDITABLE_BY_CUSTOMER).
  isSkuExportAvailable: ({
    quote,
    user,
  }: {
    quote?: IBaseQuote | null;
    user?: IAuthUser | null;
  }): boolean => {
    const isImported = QuoteStatus.EDITABLE_BY_CUSTOMER === quote?.status;

    return isImported && VendorQuoteHelper.isAlternateExportEnabled({ user });
  },

  // Attributes tab visibility, for every user whatever their rights: editing is the BE's
  // `canEditAttributes` (isAttributesEditBlocked). The Send as SPA right gates only the send.
  isAttributesAvailable: ({
    quote,
    user,
  }: {
    quote?: IBaseQuote | null;
    user?: IAuthUser | null;
  }): boolean => VendorQuoteHelper.isSkuExportAvailable({ quote, user }),

  // The import wizard's Attributes step: no quote exists yet on a first import, so only the
  // company capability counts. It is offered to every importer, not only to holders of the Send as
  // SPA right — that right gates the send itself.
  isImportAttributesStepAvailable: ({ user }: { user?: IAuthUser | null }): boolean =>
    VendorQuoteHelper.isAlternateExportEnabled({ user }),

  // Date part only: the value may come as a datetime, and a timezone-aware parse could shift the
  // day. The MySQL zero date counts as missing.
  getValidUntilDay: (quote?: Pick<IVendorQuote, 'validUntil'> | null): string | null => {
    const day = quote?.validUntil?.slice(0, 10) ?? '';

    return VALID_UNTIL_DAY_PATTERN.test(day) && day !== ZERO_DATE ? day : null;
  },

  // `today` is the user's local day (getLocalDateForServer), the same value the send carries as
  // `local_date`.
  isValidUntilInPast: (
    quote: Pick<IVendorQuote, 'validUntil'> | null | undefined,
    today: string
  ): boolean => {
    const day = VendorQuoteHelper.getValidUntilDay(quote);

    return day !== null && day < today;
  },

  // Ascending id = the Quote ID the vendor quote list shows.
  getPastValidUntilQuoteIds: (quotes: IVendorQuote[], today: string): number[] =>
    quotes
      .filter((quote) => VendorQuoteHelper.isValidUntilInPast(quote, today))
      .map(({ id }) => id)
      .sort((a, b) => a - b),

  // On Flintfox (Sent or Updated Post Sent). Unless the BE flags the quote `canResendAsSpa`, drives
  // the send block. Mirrors Quote_Quote::isTradeAgreementSent.
  isTradeAgreementSent: (status?: TradeAgreementStatus | null): boolean =>
    TradeAgreementStatus.SENT === status || TradeAgreementStatus.UPDATED_POST_SENT === status,

  // Null sent version = never sent, or sent by a job queued before versioning shipped.
  getUpdatedPostSentTooltip: (
    sentVersion?: number | null
  ): { key: string; options?: { version: number } } =>
    typeof sentVersion === 'number'
      ? { key: 'tooltipTradeAgreementUpdatedPostSentVersion', options: { version: sentVersion } }
      : { key: 'tooltipTradeAgreementUpdatedPostSent' },

  // Label + disabled state + tooltip of a Send as SPA action. Precedence: in flight, then sent and
  // unchanged, then the Attributes preflight, else enabled. A resendable quote keeps the "Resend"
  // label even when the preflight blocks it. The export drawer skips the preflight: the Attributes
  // step the user just submitted is what fixes it, and the quote's flags can lag that save. It
  // still applies the audience rights, to the selection it previewed.
  getSpaSendButtonState: ({
    quote,
    spaAudienceRights,
    isSending = false,
    skipAttributeBlockers = false,
    previewedAudience = null,
  }: {
    quote?: IVendorQuote | null;
    spaAudienceRights: ISpaAudienceRights;
    isSending?: boolean;
    skipAttributeBlockers?: boolean;
    previewedAudience?: ISpaAudience | null;
  }): ISpaSendButtonState => {
    const isResend = quote?.canResendAsSpa === true;

    if (isSending) {
      return { isResend, isDisabled: true, tooltip: { key: 'tooltipSendAsSpaInProgress' } };
    }

    if (!isResend && VendorQuoteHelper.isTradeAgreementSent(quote?.tradeAgreementStatus)) {
      const sentVersion = quote?.tradeAgreementSentVersion;
      const isSentUnchangedWithVersion =
        TradeAgreementStatus.SENT === quote?.tradeAgreementStatus &&
        typeof sentVersion === 'number';

      return {
        isResend: false,
        isDisabled: true,
        tooltip: isSentUnchangedWithVersion
          ? { key: 'tooltipSendAsSpaSentUnchanged', options: { version: sentVersion } }
          : { key: 'tooltipSendAsSpaAlreadySent' },
      };
    }

    let blockReason: SpaSendBlockReason | null;
    if (!skipAttributeBlockers) {
      blockReason = VendorQuoteHelper.getSpaSendBlockReason(quote, spaAudienceRights);
    } else if (previewedAudience) {
      blockReason = VendorQuoteHelper.getSpaAudienceBlockReason(
        previewedAudience,
        spaAudienceRights
      );
    } else {
      blockReason = null;
    }

    if (null !== blockReason) {
      return {
        isResend,
        isDisabled: true,
        tooltip: { key: spaSendBlockTooltipKeyMap[blockReason] },
      };
    }

    return { isResend, isDisabled: false, tooltip: null };
  },

  // Why a VQ's persisted Attributes make it unsendable, or null when it is sendable. Mirrors the
  // BE preflight (AttributesService::assertExportable) so the button gate and the 422 can never
  // disagree; single and bulk both classify through this.
  //
  // Reads the BE-computed flags rather than re-deriving from the attribute values: the required set
  // is evaluated against raw columns the rows do not carry, and the dates one depends on
  // `valid_until`, which the ordinary quote edit moves without touching Attributes — so a quote can
  // become unsendable with no attribute change at all.
  //
  // A project without any Partfiniti customer is only detectable server-side; the BE refuses it.
  getSpaSendBlockReason: (
    quote: IVendorQuote | null | undefined,
    spaAudienceRights: ISpaAudienceRights
  ): SpaSendBlockReason | null => {
    if (!quote) {
      return null;
    }

    // Presence before ordering, exactly as assertExportable() checks them: the dates flag stays
    // true for a blank start date, so it alone can never report a missing one.
    if (quote.attributeHasRequiredAttributes !== true) {
      return SpaSendBlockReason.MISSING_REQUIRED_ATTRIBUTES;
    }

    if (quote.attributeHasValidTradeAgreementDates !== true) {
      return SpaSendBlockReason.INVALID_TRADE_AGREEMENT_DATES;
    }

    // Last, as the BE checks it after the dates.
    return VendorQuoteHelper.getSpaAudienceBlockReason(
      VendorQuoteHelper.getStoredSpaAudience(quote),
      spaAudienceRights
    );
  },

  // Check order follows the BE: program, All Customers, then either one with All Sales Locations.
  getSpaAudienceBlockReason: (
    { allCustomers, allSalesLocations, projectKind }: ISpaAudience,
    {
      canAllowAllCustomersSpa,
      canAllowAllCustomersAllLocationsSpa,
      canAllowProgramSpa,
    }: ISpaAudienceRights
  ): SpaSendBlockReason | null => {
    const isProgram = AttributeProjectKind.PROGRAM === projectKind;

    if (!allCustomers && !isProgram) {
      return null;
    }

    if (isProgram && !canAllowProgramSpa) {
      return SpaSendBlockReason.PROGRAM_NOT_PERMITTED;
    }

    if (allCustomers && !canAllowAllCustomersSpa) {
      return SpaSendBlockReason.ALL_CUSTOMERS_NOT_PERMITTED;
    }

    if (allSalesLocations && !canAllowAllCustomersAllLocationsSpa) {
      return SpaSendBlockReason.ALL_CUSTOMERS_ALL_SALES_LOCATIONS_NOT_PERMITTED;
    }

    return null;
  },

  // Stored state, user-independent. The details payload lacks the list flags, so the stored
  // attributes stand in for them.
  getStoredSpaAudience: (quote?: IVendorQuote | null): ISpaAudience => ({
    allCustomers: quote?.attributeHasAllCustomers ?? quote?.attributes?.allCustomers === true,
    allSalesLocations:
      quote?.attributeHasAllSalesLocations ?? quote?.attributes?.allSalesLocations === true,
    projectKind:
      quote?.attributeProjectKind !== undefined
        ? quote.attributeProjectKind
        : VendorQuoteHelper.getStoredProjectKind(quote?.attributes?.project),
  }),

  // The BE sends a project of null kind (unknown external type) as All Customers, like a program.
  getStoredProjectKind: (project?: IAttributeProject | null): AttributeProjectKind | null => {
    if (!project?.id) {
      return null;
    }

    return project.kind ?? AttributeProjectKind.PROGRAM;
  },

  getSpaAudienceRights: (user?: IAuthUser | null): ISpaAudienceRights => ({
    canAllowAllCustomersAllLocationsSpa:
      user?.resources?.includes(AccessPermissions.ALLOW_ALL_CUSTOMERS_ALL_LOCATIONS_SPA) ?? false,
    canAllowAllCustomersSpa: VendorQuoteHelper.canAllowAllCustomersSpa(user),
    canAllowProgramSpa: user?.resources?.includes(AccessPermissions.ALLOW_PROGRAM_SPA) ?? false,
  }),

  canAllowAllCustomersSpa: (user?: IAuthUser | null): boolean =>
    user?.resources?.includes(AccessPermissions.ALLOW_ALL_CUSTOMERS_SPA) ?? false,
};
